<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import {
  SIDEBAR_PERMISSION_SECTIONS,
  PERMISSION_LEVELS,
} from 'dashboard/constants/sidebarPermissions';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const route = useRoute();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const currentAccountId = computed(() => Number(route.params?.accountId));

const hasFeature = featureName => {
  if (!featureName) return true;
  return isFeatureEnabled.value(currentAccountId.value, featureName);
};

const visibleSections = computed(() => {
  return SIDEBAR_PERMISSION_SECTIONS.filter(section => hasFeature(section.feature))
    .map(section => ({
      ...section,
      children: section.children.filter(child => hasFeature(child.feature)),
    }))
    .filter(section => section.children.length > 0);
});

const expandedSections = ref({});

const isExpanded = sectionId => Boolean(expandedSections.value[sectionId]);

const toggleExpand = sectionId => {
  expandedSections.value = {
    ...expandedSections.value,
    [sectionId]: !expandedSections.value[sectionId],
  };
};

const setExpanded = (sectionId, value) => {
  expandedSections.value = {
    ...expandedSections.value,
    [sectionId]: value,
  };
};

const defaultLevel = child =>
  child.access === 'editable' ? PERMISSION_LEVELS.READ : PERMISSION_LEVELS.VIEW;

const isChildSelected = childId =>
  Object.prototype.hasOwnProperty.call(props.modelValue, childId);

const childLevel = childId => props.modelValue[childId];

const selectedChildCount = section =>
  section.children.filter(child => isChildSelected(child.id)).length;

const isSectionSelected = section => selectedChildCount(section) > 0;

const isSectionPartial = section => {
  const count = selectedChildCount(section);
  return count > 0 && count < section.children.length;
};

const emitValue = next => emit('update:modelValue', next);

const toggleChild = child => {
  const next = { ...props.modelValue };
  if (isChildSelected(child.id)) {
    delete next[child.id];
  } else {
    next[child.id] = defaultLevel(child);
  }
  emitValue(next);
};

const setChildLevel = (child, level) => {
  if (!isChildSelected(child.id)) return;
  emitValue({ ...props.modelValue, [child.id]: level });
};

const toggleSection = section => {
  const next = { ...props.modelValue };
  if (isSectionSelected(section)) {
    section.children.forEach(child => {
      delete next[child.id];
    });
  } else {
    section.children.forEach(child => {
      next[child.id] = defaultLevel(child);
    });
    setExpanded(section.id, true);
  }
  emitValue(next);
};
</script>

<template>
  <div class="flex flex-col w-full gap-2">
    <div
      v-for="section in visibleSections"
      :key="section.id"
      class="border rounded-lg border-n-weak"
    >
      <div class="flex items-center gap-2 p-2">
        <input
          type="checkbox"
          :checked="isSectionSelected(section)"
          :indeterminate="isSectionPartial(section)"
          class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
          @change="toggleSection(section)"
        />
        <button
          type="button"
          class="flex items-center justify-between flex-grow gap-2 text-left"
          @click="toggleExpand(section.id)"
        >
          <span class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
            <span v-if="section.icon" :class="section.icon" class="text-base" />
            {{ $t(section.labelKey) }}
          </span>
          <span
            class="text-base text-n-slate-10"
            :class="isExpanded(section.id) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          />
        </button>
      </div>

      <div
        v-if="isExpanded(section.id)"
        class="flex flex-col gap-1 px-2 pt-1 pb-2 ltr:ml-6 rtl:mr-6"
      >
        <div
          v-for="child in section.children"
          :key="child.id"
          class="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-n-alpha-1"
        >
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              :checked="isChildSelected(child.id)"
              class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
              @change="toggleChild(child)"
            />
            <span class="text-sm text-n-slate-12">{{ $t(child.labelKey) }}</span>
          </label>

          <div
            v-if="child.access === 'editable' && isChildSelected(child.id)"
            class="flex items-center p-0.5 rounded-md bg-n-alpha-1"
          >
            <button
              type="button"
              class="px-2 py-1 text-xs rounded"
              :class="childLevel(child.id) === 'read'
                ? 'bg-n-brand text-white'
                : 'text-n-slate-11'"
              @click="setChildLevel(child, 'read')"
            >
              {{ $t('AGENT_MGMT.PERMISSIONS.READ') }}
            </button>
            <button
              type="button"
              class="px-2 py-1 text-xs rounded"
              :class="childLevel(child.id) === 'edit'
                ? 'bg-n-brand text-white'
                : 'text-n-slate-11'"
              @click="setChildLevel(child, 'edit')"
            >
              {{ $t('AGENT_MGMT.PERMISSIONS.EDIT') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
